'use client'

import { useCallback, useRef, useState } from 'react'
import type { TurnstileInstance } from '@marsidev/react-turnstile'

export function useCaptcha() {
  const ref = useRef<TurnstileInstance | null>(null)
  const [token, setToken] = useState<string | null>(null)
  // Bumped to force a full remount of the widget (used as its React `key`).
  const [renderKey, setRenderKey] = useState(0)

  // Get a brand-new widget instance, which runs a fresh challenge and issues a
  // fresh single-use token. We remount rather than call ref.reset() because
  // reset() silently no-ops ("Turnstile has not been loaded") whenever the
  // widget id isn't available at call time, leaving the already-consumed token
  // in place — Supabase then rejects the resubmitted token as
  // "timeout-or-duplicate". A remount always renders a new widget, so the next
  // submit gets a genuinely new token.
  const refresh = useCallback(() => {
    setToken(null)
    setRenderKey((k) => k + 1)
  }, [])

  // Resolve the current captcha token, WAITING for the widget to finish its
  // (possibly in-progress) challenge. getResponsePromise polls until the widget
  // is solved, so it survives the refresh-then-resubmit gap. Resolves undefined
  // when no widget is mounted (CAPTCHA disabled) or the challenge doesn't
  // complete within the timeout — callers surface that as a "click again" retry.
  const getToken = useCallback(async (timeoutMs = 15_000): Promise<string | undefined> => {
    const instance = ref.current
    if (!instance?.getResponsePromise) return undefined
    try {
      return await instance.getResponsePromise(timeoutMs)
    } catch {
      return undefined
    }
  }, [])

  return { ref, token, setToken, refresh, renderKey, getToken }
}
